import { useCallback, useRef, useState } from "react";

const MIN_PX = 80;
const DIVIDER_PX = 5;

export function useResizablePanels(initialPercents = [33.33, 33.33, 33.34]) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState(initialPercents); // [left%, mid%, right%]

  const startDrag = useCallback(
    (dividerIndex: 0 | 1) => (e: React.MouseEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const totalW = container.getBoundingClientRect().width - DIVIDER_PX * 2;
      const startX = e.clientX;
      const startWidths = [...widths];

      const onMove = (mv: MouseEvent) => {
        const dx = mv.clientX - startX;
        const dxPct = (dx / totalW) * 100;

        setWidths((prev) => {
          const next = [...prev];
          if (dividerIndex === 0) {
            // Left divider: redistribute between panel 0 and 1
            const leftPct = Math.max(
              (MIN_PX / totalW) * 100,
              Math.min(
                startWidths[0] + dxPct,
                startWidths[0] + startWidths[1] - (MIN_PX / totalW) * 100,
              ),
            );
            next[0] = leftPct;
            next[1] = startWidths[0] + startWidths[1] - leftPct;
          } else {
            // Right divider: redistribute between panel 1 and 2
            const midPct = Math.max(
              (MIN_PX / totalW) * 100,
              Math.min(
                startWidths[1] + dxPct,
                startWidths[1] + startWidths[2] - (MIN_PX / totalW) * 100,
              ),
            );
            next[1] = midPct;
            next[2] = startWidths[1] + startWidths[2] - midPct;
          }
          return next;
        });
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [widths],
  );

  return { containerRef, widths, startDrag };
}
